import React, { useState, useRef, useCallback } from "react";
import { useDispatch } from "react-redux";
import baseUrl from "../../../../../api/baseUrl";
import { usePrintSettings } from "../PrintSettingsContext";

/** Title-case for display: first letter of each word upper, rest lower (so capitalize works even when title is all caps) */
function toTitleCase(str: any): string {
  if (str == null || typeof str !== "string") return String(str ?? "");
  return str
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function PrintText({ item, title, preserveCase, centered, fontWeight, lineHeight, color }: any) {
  const dispatch = useDispatch();
  const { printAction, grabApi } = usePrintSettings();
  const [cursor, setcursor] = useState("grab");
  const dragStart = useRef<{ clientX: number; clientY: number; left: number; top: number } | null>(null);

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (dragStart.current == null || item?.title == null) return;
      const { clientX, clientY, left, top } = dragStart.current;
      const newTop = top + (e.clientY - clientY);
      const newLeft = left + (e.clientX - clientX);
      dispatch(printAction({ name: item.title, target: "top", value: newTop }));
      dispatch(printAction({ name: item.title, target: "left", value: newLeft }));
      baseUrl.patch(grabApi, {
        name: item.title,
        left: newLeft,
        top: newTop,
      });
    },
    [centered, dispatch, grabApi, item?.title, printAction]
  );

  const handleMouseUp = useCallback(() => {
    dragStart.current = null;
    setcursor("grab");
    document.removeEventListener("mousemove", handleMouseMove);
    document.removeEventListener("mouseup", handleMouseUp);
  }, [handleMouseMove]);

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    if (item?.title == null) return;
    setcursor("grabbing");
    dragStart.current = {
      clientX: e.clientX,
      clientY: e.clientY,
      left: Number(item?.left) || 0,
      top: Number(item?.top) || 0,
    };
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };
  const horizontalOffset = Number(item?.left) || 0;

  return (
    <span
      style={{
        fontWeight: fontWeight ?? 600,
        color: color ?? "#1e293b",
        position: "absolute",
        top: item?.top,
        left: centered ? 0 : item?.left,
        width: centered ? "100%" : undefined,
        textAlign: centered ? "center" : undefined,
        transform: centered ? `translateX(${horizontalOffset}px)` : undefined,
        fontSize: item?.size,
        display: item?.show ? "block" : "none",
        whiteSpace: centered ? "normal" : "nowrap",
        lineHeight: lineHeight ?? (centered ? 1.3 : undefined),
        boxSizing: "border-box",
        paddingLeft: centered ? 4 : undefined,
        paddingRight: centered ? 4 : undefined,
      }}
      className={cursor}
      onMouseDown={handleMouseDown}
    >
      {preserveCase ? title : toTitleCase(title)}
    </span>
  );
}

export default PrintText;
